import { isResource } from '@medplum/core'
import type { Practitioner, Reference } from '@medplum/fhirtypes'
import { referenceDisplay } from '@seen/core/display'
import { ExtendedPractitioner } from '@seen/core/models/practitioner'
import { cn } from '@seen/ui'
import type { JSX } from 'react'
import { Avatar } from '#src/components/core/Avatar/Avatar.tsx'
import { HoverPractitioner } from '#src/components/core/hover-card/HoverPractitioner.tsx'
import { useRead } from '#src/hooks/useRead.ts'

export interface PractitionerDisplayProps {
  practitioner: Practitioner | Reference<Practitioner> | null | undefined
  // 'stacked' renders the name as small label copy under the avatar for
  // dense layouts such as wide tables.
  variant?: 'inline' | 'stacked'
  className?: string
}

export function PractitionerDisplay({
  practitioner,
  variant = 'inline',
  className,
}: PractitionerDisplayProps): JSX.Element | null {
  const { data: extendedPractitioner } = useRead(
    ExtendedPractitioner,
    practitioner
  )
  const name = extendedPractitioner ? (
    <HoverPractitioner practitioner={extendedPractitioner} />
  ) : practitioner && !isResource(practitioner) ? (
    referenceDisplay(practitioner)
  ) : undefined

  if (variant === 'stacked') {
    return (
      <div
        className={cn(
          'flex w-16 flex-col items-center gap-0.5 text-center',
          className
        )}
      >
        <Avatar profile={practitioner ?? undefined} size="icon" />
        <span className="max-w-full truncate text-xs text-foreground-secondary">
          {name}
        </span>
      </div>
    )
  }

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <Avatar profile={practitioner ?? undefined} size="icon" />
      {name}
    </div>
  )
}
